import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Skeleton } from "@mui/material";
import { ChevronRight, CreditCard, Image as ImageIcon, ShoppingBag, ShoppingCart, Truck, ZoomIn } from "react-feather";
import { useWebsite } from "../../../../context/WebsiteContext";
import { fetchProduct, fetchProducts, formatPrice } from "../shopApi";
import { ItemBadge, PriceTag, savePercent } from "./ProductCatalogue";
import ImageLightbox from "./ImageLightbox";
import styles from "../shop.module.css";

const DetailSkeleton = () => (<div className={styles.Detail}>
  <Skeleton variant="rounded" className={styles.DetailImage} sx={{ height: "auto" }} />
  <div>
    <Skeleton width="70%" height={40} />
    <Skeleton width="30%" height={36} />
    <Skeleton width="100%" />
    <Skeleton width="90%" />
    <Skeleton variant="rounded" height={46} sx={{ mt: 3 }} />
  </div>
</div>);

// One item with its photo gallery (click to zoom), sale price, stock, a quantity picker and "You may also like".
// basePath: "/shop" or "/customer/shop".
const ProductDetail = ({ id, basePath, onAdd, adding }) => {
  const { currentDomain } = useWebsite();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [current, setCurrent] = useState(0);
  const [zoom, setZoom] = useState(null);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setItem(null);
    setNotFound(false);
    fetchProduct(currentDomain, id).then((response) => {
      if (response.data.status === "SUCCESS") {
        setItem(response.data.data);
        setCurrent(0);
        setQty(1);
      } else {
        setNotFound(true);
      }
    }).catch(() => setNotFound(true));

    // Best sellers other than this item
    fetchProducts(currentDomain, { per_page: 5, sort: "best_selling", availability: "in" }).then((response) => {
      if (response.data.status === "SUCCESS") setRelated((response.data.data || []).filter((p) => String(p.id) !== String(id)).slice(0, 4));
    }).catch(() => setRelated([]));
  }, [currentDomain, id]);

  if (notFound) {
    return (<div className={styles.EmptyState}>
      <span className={styles.EmptyIcon}><ShoppingBag size={26} /></span>
      <strong>This item is not available</strong>
      <span>It may have been removed or sold out.</span>
      <Button component={Link} to={basePath} className={styles.SecondaryBU}>Back to the shop</Button>
    </div>);
  }

  const crumbs = (<nav className={styles.Breadcrumb} aria-label="Breadcrumb">
    <Link to={basePath}>Shop</Link>
    <ChevronRight size={14} />
    <span>{item ? item.title : "Item"}</span>
  </nav>);

  if (!item) return (<>{crumbs}<DetailSkeleton /></>);

  const images = item.images?.length ? item.images : [item.image, ...(item.gallery || [])].filter(Boolean);
  const maxQty = item.max_qty === null ? 99 : item.max_qty;
  const save = savePercent(item);

  return (<>
    {crumbs}
    <div className={styles.Detail}>
      <div className={styles.DetailGallery}>
        {images.length > 0
          ? <button type="button" className={styles.DetailImageBU} onClick={() => setZoom(current)} aria-label="Zoom photo">
            <img src={images[current]} alt={item.title} className={styles.DetailImage} />
            <span className={styles.ZoomHint}><ZoomIn size={16} /></span>
            <ItemBadge item={item} />
          </button>
          : <div className={`${styles.DetailImage} ${styles.NoImage}`}><ImageIcon size={48} /></div>}
        {images.length > 1 && (<div className={styles.Thumbs}>
          {images.map((src, i) => (<button type="button" key={src} className={`${styles.ThumbBU} ${i === current ? styles.ThumbActive : ""}`} onClick={() => setCurrent(i)} aria-label={`Show photo ${i + 1}`}>
            <img src={src} alt="" />
          </button>))}
        </div>)}
      </div>

      <div className={styles.DetailInfo}>
        <h1 className={styles.DetailTitle}>{item.title}</h1>
        {item.sku && <p className={styles.PageSub}>SKU: {item.sku}</p>}

        <div className={styles.DetailPrice}>
          <span className={item.on_sale ? styles.SalePrice : undefined}>{formatPrice(item.price)}</span>
          {item.on_sale && <s className={styles.WasPrice}>{formatPrice(item.compare_at_price)}</s>}
          {save > 0 && <span className={styles.SaveTag}>Save {formatPrice(Number(item.compare_at_price) - Number(item.price))} ({save}%)</span>}
        </div>

        <div className={`${styles.Stock} ${!item.in_stock ? styles.StockOut : item.low_stock ? styles.StockLow : ""}`}>
          <span className={styles.StockDot} />
          {!item.in_stock ? "Sold out" : item.low_stock ? `Only ${item.max_qty} left in stock` : "In stock, ready to deliver"}
        </div>

        {item.in_stock
          ? (<div className={styles.QtyRow}>
            <div className={styles.Qty}>
              <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={qty <= 1} aria-label="Fewer">−</button>
              <span aria-live="polite">{qty}</span>
              <button type="button" onClick={() => setQty((q) => Math.min(maxQty, q + 1))} disabled={qty >= maxQty} aria-label="More">+</button>
            </div>
            <Button className={`${styles.PrimaryBU} ${styles.AddBU}`} size="large" startIcon={<ShoppingCart size={18} />} disabled={adding} onClick={() => onAdd(item, qty)}>
              {adding ? "Adding…" : `Add to cart · ${formatPrice(Number(item.price) * qty)}`}
            </Button>
          </div>)
          : <Button className={styles.SecondaryBU} size="large" fullWidth disabled>Sold out</Button>}

        <div className={styles.Perks}>
          <div><Truck size={18} /><span><strong>Delivered to your door</strong>Our own drivers bring your order.</span></div>
          <div><CreditCard size={18} /><span><strong>Pay on delivery or bank transfer</strong>Nothing is charged online.</span></div>
        </div>

        {item.description && (<div className={styles.DetailSection}>
          <h2>Description</h2>
          <div className={styles.Description}>{item.description}</div>
        </div>)}
      </div>
    </div>

    {related.length > 0 && (<section className={styles.Related}>
      <h2 className={styles.RelatedTitle}>You may also like</h2>
      <div className={styles.Grid}>
        {related.map((p) => (<Link to={`${basePath}/${p.id}`} className={styles.Card} key={p.id}>
          <span className={styles.CardImageLink}>
            {p.images?.length > 0 ? <img src={p.images[0]} alt="" className={styles.CardImage} loading="lazy" /> : <span className={styles.NoImage}><ImageIcon size={32} /></span>}
            <ItemBadge item={p} />
          </span>
          <span className={styles.CardBody}>
            <span className={styles.CardTitle}>{p.title}</span>
            <PriceTag item={p} />
          </span>
        </Link>))}
      </div>
    </section>)}

    <ImageLightbox images={images} index={zoom} onChange={setZoom} onClose={() => setZoom(null)} title={item.title} />
  </>);
};

export default ProductDetail;
